import { zodResolver } from '@hookform/resolvers/zod';
import GppGoodRounded from '@mui/icons-material/GppGoodRounded';
import GppMaybeRounded from '@mui/icons-material/GppMaybeRounded';
import PasswordRounded from '@mui/icons-material/PasswordRounded';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { api, unwrap } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ROLE_LABELS } from '../../auth/capabilities';
import { PasswordField } from '../../auth/PasswordField';
import { PageHeader } from '../../components/states';
import { Field, IconBadge, Panel } from '../../components/ui';
import { dateTime, initials, relativeTime } from '../../lib/format';
import { useNotify } from '../../lib/notify';
import { accountErrorMessage, PASSWORD_MAX, PASSWORD_MIN, passwordStrength } from './messages';

/** `/account`: profile, two-step verification and password. */
export function AccountPage() {
  const { me } = useAuth();
  if (!me) return null;
  return (
    <>
      <PageHeader
        title="Account & security"
        subtitle="Your platform staff account. Changes here are recorded in the platform audit log."
      />
      <Box
        sx={{
          display: 'grid',
          gap: 2.5,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) minmax(0,1.2fr)' },
          alignItems: 'start',
        }}
      >
        <Stack spacing={2.5}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 56,
                    height: 56,
                    fontSize: 20,
                    color: '#fff',
                    background: 'linear-gradient(135deg, #0F8A73, #05473E)',
                  }}
                >
                  {initials(me.name)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" component="h2" noWrap>
                    {me.name}
                  </Typography>
                  <Typography color="text.secondary" noWrap>
                    {me.email}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
                <Field label="Role">
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={ROLE_LABELS[me.role]}
                  />
                </Field>
                <Field label="Last sign-in">
                  {me.lastLoginAt ? (
                    <span title={dateTime(me.lastLoginAt)}>{relativeTime(me.lastLoginAt)}</span>
                  ) : (
                    '—'
                  )}
                </Field>
              </Box>
            </CardContent>
          </Card>
          <MfaCard />
        </Stack>
        <ChangePasswordCard />
      </Box>
    </>
  );
}

function MfaCard() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [disabling, setDisabling] = useState(false);
  if (!me) return null;
  const on = me.mfaEnabled;
  return (
    <Panel
      title="Two-step verification"
      subtitle="A code from your authenticator app at every sign-in"
      action={
        <Chip
          size="small"
          label={on ? 'On' : 'Off'}
          color={on ? 'success' : 'warning'}
          variant="outlined"
        />
      }
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <IconBadge
          icon={on ? <GppGoodRounded /> : <GppMaybeRounded />}
          tone={on ? 'success' : 'warning'}
        />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {on
              ? 'Your account is protected. If you lose your phone, sign in with one of your recovery codes.'
              : 'Anyone with your password can sign in. Turn on two-step verification to protect tenant data.'}
          </Typography>
          {me.mfaRequired && on && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              Required on this server: if you turn it off you will be asked to set it up again.
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            {on ? (
              <Button variant="outlined" color="error" onClick={() => setDisabling(true)}>
                Turn off
              </Button>
            ) : (
              <Button variant="contained" onClick={() => void navigate('/account/mfa')}>
                Set up two-step verification
              </Button>
            )}
          </Box>
        </Box>
      </Stack>
      <DisableMfaDialog open={disabling} onClose={() => setDisabling(false)} />
    </Panel>
  );
}

const disableSchema = z.object({
  password: z.string().min(1, 'Enter your password'),
  code: z
    .string()
    .trim()
    .min(6, 'Enter the 6-digit code or a recovery code')
    .max(20, 'Enter the 6-digit code or a recovery code'),
});

function DisableMfaDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { reloadMe } = useAuth();
  const notify = useNotify();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof disableSchema>>({
    resolver: zodResolver(disableSchema),
    defaultValues: { password: '', code: '' },
  });
  const close = () => {
    reset();
    setError(null);
    onClose();
  };
  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      await unwrap(api.POST('/platform/me/mfa/disable', { body: values }));
      await reloadMe();
      notify.success('Two-step verification is off');
      close();
    } catch (e) {
      setError(accountErrorMessage(e));
    }
  });
  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : close} maxWidth="xs" fullWidth>
      <form noValidate onSubmit={(e) => void onSubmit(e)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Turn off two-step verification?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Your account will be protected by your password only. Confirm with your password and a
              current code (or a recovery code).
            </Typography>
            {error && <Alert severity="error">{error}</Alert>}
            <PasswordField
              size="small"
              label="Password"
              autoComplete="current-password"
              registration={register('password')}
              error={errors.password?.message}
            />
            <TextField
              label="Code"
              placeholder="123456 or xxxx-xxxx"
              autoComplete="one-time-code"
              {...register('code')}
              error={Boolean(errors.code)}
              helperText={errors.code?.message}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={close} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="error" loading={isSubmitting}>
            Turn off
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: z
      .string()
      .min(PASSWORD_MIN, `Use at least ${String(PASSWORD_MIN)} characters`)
      .max(PASSWORD_MAX, `Use at most ${String(PASSWORD_MAX)} characters`),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, {
    path: ['confirm'],
    message: 'The passwords do not match',
  })
  .refine((v) => v.newPassword !== v.currentPassword, {
    path: ['newPassword'],
    message: 'Choose a password different from the current one',
  });

const STRENGTH = [
  { label: 'Too short', color: 'error.main' },
  { label: 'Fair', color: 'warning.main' },
  { label: 'Good', color: 'info.main' },
  { label: 'Strong', color: 'success.main' },
  { label: 'Excellent', color: 'success.main' },
] as const;

function ChangePasswordCard() {
  const notify = useNotify();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof passwordSchema>>({
    resolver: zodResolver(passwordSchema),
    mode: 'onBlur',
    defaultValues: { currentPassword: '', newPassword: '', confirm: '' },
  });
  const pw = watch('newPassword');
  const strength = passwordStrength(pw);
  const meta = STRENGTH[strength] ?? STRENGTH[0];

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      await unwrap(
        api.POST('/platform/me/password', {
          body: { currentPassword: values.currentPassword, newPassword: values.newPassword },
        }),
      );
      reset();
      notify.success('Password changed. Your other sessions were signed out.');
    } catch (e) {
      setError(accountErrorMessage(e));
    }
  });

  return (
    <Panel
      title="Change password"
      subtitle={`${String(PASSWORD_MIN)}–${String(PASSWORD_MAX)} characters. Other signed-in sessions are signed out.`}
      action={<IconBadge icon={<PasswordRounded />} size={36} />}
    >
      <form noValidate onSubmit={(e) => void onSubmit(e)}>
        <Stack spacing={2.25}>
          {error && <Alert severity="error">{error}</Alert>}
          <PasswordField
            label="Current password"
            autoComplete="current-password"
            registration={register('currentPassword')}
            error={errors.currentPassword?.message}
          />
          <Box>
            <PasswordField
              label="New password"
              autoComplete="new-password"
              registration={register('newPassword')}
              error={errors.newPassword?.message}
            />
            {pw && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 1 }}>
                <Stack direction="row" spacing={0.5} sx={{ flex: 1 }} aria-hidden>
                  {[1, 2, 3, 4].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        height: 4,
                        borderRadius: 4,
                        bgcolor: i <= strength ? meta.color : 'action.selected',
                        transition: 'background-color 160ms',
                      }}
                    />
                  ))}
                </Stack>
                <Typography
                  variant="caption"
                  sx={{ color: meta.color, fontWeight: 650, minWidth: 64 }}
                >
                  {meta.label}
                </Typography>
              </Stack>
            )}
          </Box>
          <PasswordField
            label="Confirm new password"
            autoComplete="new-password"
            registration={register('confirm')}
            error={errors.confirm?.message}
          />
          <Box>
            <Button type="submit" variant="contained" loading={isSubmitting}>
              Change password
            </Button>
          </Box>
        </Stack>
      </form>
    </Panel>
  );
}
